/* global route */

import Vuex, { Store } from 'vuex';

import { AdminUsers } from './Admin/Users';
import { Ai } from './Ai';
import { BannersList } from './BannersList';
import { CaseCollection } from './CaseCollection';
import { CaseFeed } from './CaseFeed';
import { CaseGroup } from './CaseGroup';
import { CaseQuiz } from './CaseQuiz';
import { CaseQuizForms } from './CaseQuizForms';
import { CaseQuizList } from './CaseQuizList';
import { CaseView } from './CaseView';
import { CasesFiltersList } from './CasesFiltersList';
import { CollectionView } from './CollectionView';
import { CommonResources } from './CommonResources';
import { DicomViewer } from './DicomViewer';
import { Forms } from './Forms';
import { Meddream } from './Meddream';
import { MediaFeed } from './MediaFeed';
import { MultiListFeed } from './MultiListFeed';
import { Packages } from './Packages';
import { PresenceChannel } from './PresenceChannel';
import { Research } from './Research';
import { Thumbnail } from './Thumbnail';
import { UploadDeadletter } from './UploadDeadletter';
import { VET_GROUP_SLUG } from '@/config/veheri';
import Vue from 'vue';
import { Webupload } from './Webupload';
import axios from 'axios';

Vue.use(Vuex);

export default new Store({
  strict: process.env.NODE_ENV !== 'production',

  modules: {
    commonResources: CommonResources,
    casesFiltersList: CasesFiltersList,
    caseFeed: CaseFeed,
    bannersList: BannersList,
    caseGroup: CaseGroup,
    caseQuiz: CaseQuiz,
    caseQuizForms: CaseQuizForms,
    caseQuizList: CaseQuizList,
    collectionView: CollectionView,
    caseCollection: CaseCollection,
    caseView: CaseView,
    MultiListFeed: MultiListFeed,
    mediaFeed: MediaFeed,
    meddream: Meddream,
    thumbnail: Thumbnail,
    dicomViewer: DicomViewer,
    ai: Ai,
    forms: Forms,
    adminUsers: AdminUsers,
    packages: Packages,
    research: Research,
    presence: PresenceChannel,
    webupload: Webupload,
    uploadDeadletter: UploadDeadletter,
  },

  state: {
    researchRepositoryUuid: null,
    theme: 'dark',
    busy: false,
    currentUser: null,
    currentUserResource: null,
    login: {
      open: false,
      email: null,
    },
    isCookieAccepted: false,
    isShowingCookieToast: false,
    viewerOpen: false,
    openPopup: null,
    searchText: null,
    userGroups: [],
    breakpoint: null,
    following: [],
    followingFetched: false,
    hospitalList: [],
    sidebarOpen: localStorage.getItem('CollectiveMinds::SidebarOpen')
      ? localStorage.getItem('CollectiveMinds::SidebarOpen') === 'true'
      : null,
    repositories: null,
    isGettingRepositories: false,
    featureFlags: [],
    loadedFeatureFlags: false,
    tenant: null,
    env: null,
    isRestrictedUser: false,
    menuSize: 50,
    loggedIn: false,
    jwtCookieName: 'auth_service_jwt',
  },

  getters: {
    busy: (state) => state.busy,
    authenticated: (state) => !!state.currentUser,
    getCurrentUser: ({ currentUser }) => currentUser,
    getCurrentUserResource: ({ currentUserResource }) => currentUserResource,
    getCurrentUserResourceId: ({ currentUserResource }) => currentUserResource?.id,
    followingIds: ({ following }) => (following ? following.map((user) => user.id) : []),
    hospitalList: (state) => state.hospitalList,
    getResearchRepositoryUuid: (state) => {
      return state.researchRepositoryUuid;
    },
    getResearchRepositories: ({ repositories }) => repositories ?? [],
    isGettingRepositories: ({ isGettingRepositories }) => isGettingRepositories,
    isSidebarOpen: ({ sidebarOpen }) => sidebarOpen,
    isAdmin: ({ currentUserResource }) => currentUserResource.can.admin,
    isDarkThemeEnabled: ({ theme }) => theme === 'dark',
    getTheme: ({ theme }) => theme,
    isFeatureEnabled:
      ({ featureFlags }) =>
      (flag) =>
        featureFlags.includes(flag),
    loadedFeatureFlags: ({ loadedFeatureFlags }) => loadedFeatureFlags,
    tenant: ({ tenant }) => tenant,
    isVeheri: ({ tenant }) => tenant === 'veheri',
    isProd: ({ env }) => env === 'production',
    isRestrictedUser: ({ isRestrictedUser }) => isRestrictedUser,
    isVeheriRestrictedUser: (_, getters) => getters.isVeheri && getters.isRestrictedUser,
    groups: ({ userGroups }) => userGroups,
    vetGroup: (_, getters) => getters.groups.find(({ slug }) => slug === VET_GROUP_SLUG),
    vSelectGroupOptions: (_, getters) =>
      getters.groups.map(({ id, name }) => ({
        value: id + '',
        text: name,
      })),
    tosUrl: (_, { isVeheri }) => `https://about.${isVeheri ? 'veheri' : 'cmrad'}.com/terms-of-service/`,
    privacyPolicyUrl: (_, { isVeheri }) => `https://about.${isVeheri ? 'veheri' : 'cmrad'}.com/privacy-policy/`,
    aboutUsUrl: (_, { isVeheri }) => `https://about.${isVeheri ? 'veheri' : 'cmrad'}.com/privacy-policy/`,
    linkedInUrl: (_, { isVeheri }) => `https://www.linkedin.com/company/${isVeheri ? 'veheri' : 'cmrad'}/`,
    youtubeUrl: (_, { isVeheri }) => `https://www.youtube.com/@${isVeheri ? 'veheriexplanationvideo' : 'cmradiology'}/`,
    instagramUrl: (_, { isVeheri }) => `https://www.instagram.com/${isVeheri ? 'veherivet' : 'cmradiology'}/?hl=en`,
    productsLink: (_, { isVeheri }) => `https://about.${isVeheri ? 'veheri' : 'cmrad'}.com/`,
    facebookUrl: (_, { isVeheri }) => `https://www.facebook.com/${isVeheri ? 'Veheri-100384526153008' : 'cmradiology'}`,
    menuSize: ({ menuSize }) => menuSize,
    menuButtonSize: ({ menuSize }) => (menuSize > 70 ? 'lg' : menuSize > 60 ? 'md' : menuSize > 50 ? 'sm' : 'xs'),
    displayShrinkingMenus: ({ breakpoint }) => ['sm', 'xs'].includes(breakpoint),
    getSpaBaseUrl:
      ({ tenant }, { isProd }) =>
      (spaDomain) =>
        `https://${spaDomain}${!isProd ? '.dev' : ''}.${tenant}.com`,
    backofficeBaseUrl: (_, { getSpaBaseUrl }) => getSpaBaseUrl('backoffice'),
    educationBaseUrl: (_, { getSpaBaseUrl }) => getSpaBaseUrl('education'),
    getJwtCookieName: ({ jwtCookieName }) => jwtCookieName,
  },

  actions: {
    setSidebarNavOpen({ commit }, status) {
      commit('setSidebarNavOpen', status);
    },

    async updateFollowingUsers({ state, commit }, force = false) {
      if (!state.followingFetched || force) {
        commit('setFollowingFetched', true);
        const user = state.currentUserResource;
        const url = route('users.following', { version: '1.0', radiologist: user.id }, false);

        try {
          const response = await axios.get(url);
          commit('setFollowing', response.data.data);
        } catch (err) {
          console.error(err);
        }
      }
    },

    async getRepositories({ commit }) {
      commit('setIsGettingRepositories', true);
      const response = await this._vm.$api('repositories.index', {
        version: '1.0',
      });

      if (response.data) {
        commit('setRepositories', response.data.data);
        commit('setIsGettingRepositories', false);
      }
    },

    async toggleLoginModal({ commit }, state = false) {
      commit('setLoginOpenStatus', state);
    },

    async toggleFeatureFlag({ commit }, flagName) {
      commit('toggleFeatureFlag', flagName);
    },

    async getFeatureFlags({ commit }) {
      const {
        data: { data: flags },
      } = await this._vm.$api('feature-flags', {
        version: '1.0',
        offset: 2,
      });
      commit(
        'setFeatureFlags',
        flags.filter(({ enabled }) => enabled).map(({ feature }) => feature)
      );
      commit('setLoadedFeatureFlags', true);
    },
  },

  mutations: {
    setBusy(state, busy) {
      state.busy = busy;
    },
    setFollowingFetched(state, status) {
      state.followingFetched = status;
    },

    followUser(state, user) {
      state.following = [...state.following, user];
    },

    unfollowUser(state, user) {
      state.following = state.following.filter(({ id }) => id != user.id);
    },

    setFollowing(state, newFollowing) {
      state.following = newFollowing;
    },

    setSearchText(state, text) {
      state.searchText = text;
    },

    setViewerOpen(state, status) {
      state.viewerOpen = status;
    },

    setRepositories(state, repositories) {
      state.repositories = repositories;
    },

    setIsGettingRepositories(state, status) {
      state.isGettingRepositories = status;
    },

    setOpenPopup(state, popup) {
      state.openPopup = popup;
    },

    setTheme(state, theme = 'light') {
      state.theme = theme;
    },

    setHospitalsList(state, hospitals = []) {
      state.hospitalList = hospitals;
    },

    updateCurrentUser(state, newUser) {
      state.currentUser = newUser ? Object.assign({}, state.currentUser, newUser) : null;
    },

    updateUserGroups(state, groups) {
      state.userGroups = groups;
    },

    updateCurrentUserResource(state, newUser) {
      state.currentUserResource = newUser ? Object.assign({}, state.currentUserResource, newUser) : null;
    },

    openLoginModal(state, email = null) {
      state.login = Object.assign({}, state.login, { open: true, email });
    },

    setLoginOpenStatus(state, open) {
      state.login = { ...state.login, open };
    },

    loginModalOpened(state) {
      state.login = Object.assign({}, state.login, {
        open: false,
      });
    },

    setCookieStatus(state, status) {
      state.isCookieAccepted = status;
    },

    setCookieToastDisplayStatus(state, status) {
      state.isShowingCookieToast = status;
    },

    setBreakpoint(state, breakpoint) {
      state.breakpoint = breakpoint;
    },

    addRepositoryUuid(state, uuid) {
      state.researchRepositoryUuid = uuid;
    },

    setSidebarNavOpen(state, status) {
      state.sidebarOpen = status;
      localStorage.setItem('CollectiveMinds::SidebarOpen', status);
    },

    setFeatureFlags(state, flags) {
      state.featureFlags = flags;
    },

    setLoadedFeatureFlags(state, bool) {
      Vue.set(state, 'loadedFeatureFlags', bool);
    },

    setTenant(state, tenant) {
      Vue.set(state, 'tenant', tenant);
    },

    setEnv(state, env) {
      Vue.set(state, 'env', env);
    },

    toggleFeatureFlag(state, flagName) {
      if (state.featureFlags.includes(flagName)) {
        Vue.delete(state.featureFlags, state.featureFlags.indexOf(flagName));
      } else {
        Vue.set(state.featureFlags, state.featureFlags.length, flagName);
      }
    },

    setMenuSize(state, px) {
      Vue.set(state, 'menuSize', px);
    },

    setLoggedIn(state, bool) {
      Vue.set(state, 'loggedIn', bool);
    },
    setJwtCookieName(state, name) {
      Vue.set(state, 'jwtCookieName', name);
    },
  },
});
